/**
 * Grid operations utilities
 * Handles cell value updates, formatting, and calculations
 */

/**
 * Parse a number that might contain Norwegian thousand separators (spaces)
 * @param {string} value - Raw value with possible spaces
 * @returns {number} Parsed number or NaN if invalid
 */
const parseNorwegianNumber = (value) => {
  if (!value || typeof value !== 'string') return NaN;
  // Remove spaces (Norwegian thousand separator) and commas before parsing
  const cleanValue = value.replace(/\s/g, '').replace(/,/g, '.');
  return parseInt(cleanValue);
};

/**
 * Format display value for algae cells
 * @param {string} value - Raw value
 * @returns {string} Formatted value
 */
export const formatDisplayValue = (value) => {
  if (!value || value === '') return '';
  if (value === '<5000') return '<5000';
  
  const numValue = parseNorwegianNumber(value);
  if (isNaN(numValue)) return value;
  
  return numValue.toLocaleString('nb-NO');
};

/**
 * Get raw number value from potentially formatted string
 * @param {string} value - Formatted or raw value
 * @returns {string} Raw number as string (no spaces)
 */
export const getRawValue = (value) => {
  if (!value || value === '') return '';
  if (value === '<5000') return '<5000';
  
  const numValue = parseNorwegianNumber(value);
  if (isNaN(numValue)) return value;
  
  return numValue.toString();
};

/**
 * Format display value for zooplankton cells
 * @param {string} value - Raw value
 * @returns {string} Formatted value
 */
export const formatZooplanktonDisplayValue = (value) => {
  if (!value || value === '') return '';
  
  // Return as is for text-based values like "Få" or "Mange"
  const textValues = ['Få', 'Mange', 'Svært mange', 'få', 'mange', 'svært mange'];
  if (textValues.some(tv => value.toLowerCase().includes(tv.toLowerCase()))) {
    return value;
  }
  
  // Format numbers
  const numValue = parseNorwegianNumber(value);
  if (!isNaN(numValue)) {
    return numValue.toLocaleString('nb-NO');
  }
  
  return value;
};

/**
 * Normalize zooplankton value for storage
 * @param {string} value - Display value
 * @returns {string} Normalized storage value
 */
export const normalizeZooplanktonForStorage = (value) => {
  if (!value || value === '') return '';
  
  // Remove thousand separators
  return value.replace(/,/g, '');
};

/**
 * Calculate sums for organism groups
 * @param {Object} data - Grid data
 * @returns {Object} Updated data with sums
 */
export const calculateSums = (data) => {
  // This is a placeholder - actual implementation depends on your data structure
  // You can expand this based on your specific sum calculation logic
  return data;
};

/**
 * Update grid value with validation
 * @param {Object} gridData - Current grid data
 * @param {string} organismKey - Organism key
 * @param {string} location - Location key
 * @param {string} value - New value
 * @returns {Object} Updated grid data
 */
export const updateGridValue = (gridData, organismKey, location, value) => {
  const newData = { ...gridData };
  
  if (!newData[organismKey]) {
    newData[organismKey] = {};
  }
  
  // Clean the value - remove spaces (Norwegian thousand separator) for storage
  let cleanValue = value;
  if (value && typeof value === 'string' && value !== '<5000') {
    // Remove spaces and keep only numbers
    cleanValue = value.replace(/\s/g, '');
  }
  
  newData[organismKey] = {
    ...newData[organismKey],
    [location]: cleanValue
  };
  
  return newData;
};

/**
 * Update zooplankton grid value
 * @param {Object} zooplanktonGridData - Current zooplankton grid data
 * @param {string} organismKey - Organism key
 * @param {string} location - Location key  
 * @param {string} value - New value
 * @returns {Object} Updated zooplankton grid data
 */
export const updateZooplanktonValue = (zooplanktonGridData, organismKey, location, value) => {
  const newData = { ...zooplanktonGridData };
  
  if (!newData[organismKey]) {
    newData[organismKey] = {};
  }
  
  const normalized = normalizeZooplanktonForStorage(value);
  
  newData[organismKey] = {
    ...newData[organismKey],
    [location]: normalized
  };
  
  return newData;
};

/**
 * Apply pasted data to grid
 * @param {Array<Array<string>>} pastedData - 2D array of pasted data
 * @param {Array} organisms - List of organisms
 * @param {Array} locations - List of locations
 * @param {Object} currentGridData - Current grid data
 * @returns {Object} Updated grid data
 */
export const applyPastedDataToGrid = (pastedData, organisms, locations, currentGridData) => {
  const newGridData = { ...currentGridData };
  
  // Apply data row by row, column by column
  pastedData.forEach((row, rowIndex) => {
    if (rowIndex >= organisms.length) return;
    
    const organism = organisms[rowIndex];
    if (organism.isSum || organism.isTotal) return;
    
    const organismKey = `${organism.group}_${organism.species}`;
    
    row.forEach((cellValue, colIndex) => {
      if (colIndex >= locations.length) return;
      
      const location = locations[colIndex];
      
      if (!newGridData[organismKey]) {
        newGridData[organismKey] = {};
      }
      
      newGridData[organismKey][location] = cellValue;
    });
  });
  
  return newGridData;
};
